'use client';

import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslations } from 'next-intl';
import Link from 'next/link';

import { FacebookEmbed, InstagramEmbed, XEmbed } from 'react-social-media-embed';

import StyledSocialPost from './socialPost.style';
import Headline from '../../atoms/headline/headline';
import Copy from '../../atoms/copy/copy';

const SocialPost = ({ socialPostUrl, type, moreInfoLink }) => {
  const t = useTranslations();
  const ref = useRef(null);

  const [socialEmbedsAccepted, setSocialEmbedsAccepted] = useState(false);

  useEffect(() => {
    const checkConsent = () => {
      if (window.CookieFirst && window.CookieFirst.consent?.functional) {
        setSocialEmbedsAccepted(true);
      }
    };

    checkConsent();

    window.addEventListener('cf_init', checkConsent);
    window.addEventListener('cf_consent', checkConsent);

    return () => {
      window.removeEventListener('cf_init', checkConsent);
      window.removeEventListener('cf_consent', checkConsent);
    };
  }, []);

  const handleAcceptSocialEmbeds = () => {
    if (typeof window !== 'undefined' && window.CookieFirst) {
      window.CookieFirst.acceptCategory('functional');
    }
    setSocialEmbedsAccepted(true);
  };

  const renderSocialPost = () => {
    switch (type) {
      case 'facebook':
        return <FacebookEmbed url={socialPostUrl} width="100%" />;
      case 'instagram':
        return <InstagramEmbed url={socialPostUrl} width="100%" captioned />;
      case 'twitter':
        return <XEmbed url={socialPostUrl} width="100%" />;
      case 'spotify': {
        // Spotify is not in react-social-media-embed, so we use an iframe instead
        let useUrl = socialPostUrl;
        if (!socialPostUrl.includes('/embed/')) {
          useUrl = socialPostUrl.replace(
            'https://open.spotify.com/',
            'https://open.spotify.com/embed/'
          );
        }
        return (
          <iframe
            src={useUrl}
            width="100%"
            height="352"
            frameBorder="0"
            allowFullScreen=""
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
          />
        );
      }
      default:
        return null;
    }
  };

  return (
    <StyledSocialPost ref={ref}>
      {!socialEmbedsAccepted ? (
        <div className="social-post__overlay">
          <Headline class="social-post__headline" tag="h3" tertiary>
            {t('socialPost.headline')}
          </Headline>
          <Copy>{t('socialPost.copy')}</Copy>
          <button
            type="button"
            onClick={handleAcceptSocialEmbeds}
            className="social-post__overlay-button"
          >
            {t('socialPost.cta')}
          </button>
          <Copy extraSmall>
            {t('socialPost.consent')}
            <Link
              href={{ pathname: '/universal', query: { uri: moreInfoLink } }}
              as={moreInfoLink}
              className="social-post__privacy-protection"
              role="link"
            >
              {t('socialPost.privacyProtection')}
            </Link>
          </Copy>
        </div>
      ) : (
        <div className="social-post__content">{renderSocialPost()}</div>
      )}
    </StyledSocialPost>
  );
};

SocialPost.propTypes = {
  type: PropTypes.string.isRequired,
  socialPostUrl: PropTypes.string.isRequired,
  moreInfoLink: PropTypes.string.isRequired
};

export default SocialPost;
